import { useCallback } from 'react';
import { NavItem } from '../config/navigationConfig.types';
import { useApplicationContext } from '../context/ApplicationContext';

/**
 * useActiveNavItem - Determines which navigation item is currently active
 * 
 * Uses a hybrid approach:
 * 1. Primary: Explicit activeNavItemId from context (set during navigation)
 * 2. Fallback: Infer from activeView state (for postMessage navigation)
 * 3. Last resort: URL pattern matching (for external/legacy apps)
 * 
 * Returns the currently active nav item ID and a setter function.
 */
export function useActiveNavItem(navItems: NavItem[]) {
  const { activeView, currentProgram } = useApplicationContext();

  /**
   * Find active item by matching view state to navigation routes
   */
  const inferActiveItemFromState = useCallback(
    (items: NavItem[]): string | null => {
      for (const item of items) {
        // Skip items without routes
        if (!item.route) {
          // Still check children for accordion-only items
          if (item.children) {
            const childMatch = inferActiveItemFromState(item.children);
            if (childMatch) return childMatch;
          }
          continue;
        }

        // Match by route type
        switch (item.route.type) {
          case 'internal':
            if (activeView.type === 'landing') {
              const targetSection = item.route.path === '/' ? 'home' : item.id;
              if (activeView.section === targetSection) {
                return item.id;
              }
            }
            break;

          case 'program':
            if (item.route.program === currentProgram && activeView.type === 'iframe') {
              return item.id;
            }
            break;

          case 'component':
            if (
              activeView.type === 'component' &&
              activeView.component === item.route.component
            ) {
              return item.id;
            }
            break;

          case 'envKey':
            // Match by section for environment-based routes
            if (activeView.type === 'iframe' || activeView.type === 'landing') {
              // First try exact section match
              if (activeView.section === item.id) {
                return item.id;
              }
              
              // Fallback: match by URL pattern if available
              if (activeView.url && item.route.envKey) {
                const urlLower = activeView.url.toLowerCase();
                const envKeyLower = item.route.envKey.toLowerCase();
                if (urlLower.includes(envKeyLower)) {
                  return item.id;
                }
              }
            }
            break;

          case 'external':
            // External links are only active when explicitly set
            // (we don't track external navigation)
            break;
        }

        // Recursively check children
        if (item.children) {
          const childMatch = inferActiveItemFromState(item.children);
          if (childMatch) return childMatch;
        }
      }
      return null;
    },
    [activeView, currentProgram],
  );

  /**
   * Find if a nav item or any of its descendants match the given ID
   */
  const findItemById = useCallback(
    (items: NavItem[], targetId: string): NavItem | null => {
      for (const item of items) {
        if (item.id === targetId) return item;
        if (item.children) {
          const found = findItemById(item.children, targetId);
          if (found) return found;
        }
      }
      return null;
    },
    [],
  );

  /**
   * Check if a nav item has a descendant with the given ID
   */
  const hasActiveDescendant = useCallback(
    (item: NavItem, activeId: string | null): boolean => {
      if (!activeId || !item.children) return false;
      return !!findItemById(item.children, activeId);
    },
    [findItemById],
  );

  /**
   * Get the inferred active item ID
   */
  const getActiveItemId = useCallback((): string | null => {
    return inferActiveItemFromState(navItems);
  }, [navItems, inferActiveItemFromState]);

  return {
    getActiveItemId,
    hasActiveDescendant,
    findItemById,
  };
}
